import { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Calendar, Plus, Upload, Link as LinkIcon, Share2, ChevronLeft, ChevronRight, MapPin, Clock, User, Trash2, Pencil, X, Save, Smartphone } from "lucide-react";
import axios from "axios";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Checkbox } from "@/components/ui/checkbox";
import CalendarSourcesDialog from "@/components/calendar/CalendarSourcesDialog";
import SpellCheckButton from "@/components/SpellCheckButton";
import { toast } from "sonner";

// Colors used per-member (deterministic hash → palette index)
const MEMBER_PALETTE = [
  { bg: "bg-sky-100", text: "text-sky-800", dot: "bg-sky-500", hex: "#0ea5e9" },
  { bg: "bg-amber-100", text: "text-amber-800", dot: "bg-amber-500", hex: "#f59e0b" },
  { bg: "bg-rose-100", text: "text-rose-800", dot: "bg-rose-500", hex: "#f43f5e" },
  { bg: "bg-emerald-100", text: "text-emerald-800", dot: "bg-emerald-500", hex: "#10b981" },
  { bg: "bg-violet-100", text: "text-violet-800", dot: "bg-violet-500", hex: "#8b5cf6" },
  { bg: "bg-orange-100", text: "text-orange-800", dot: "bg-orange-500", hex: "#f97316" },
  { bg: "bg-teal-100", text: "text-teal-800", dot: "bg-teal-500", hex: "#14b8a6" },
  { bg: "bg-pink-100", text: "text-pink-800", dot: "bg-pink-500", hex: "#ec4899" },
];

function colorFor(assigneeId, assigneeName) {
  const key = assigneeId || assigneeName || "";
  if (!key) return { bg: "bg-stone-100", text: "text-stone-700", dot: "bg-stone-400", hex: "#78716c" };
  let hash = 0;
  for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) >>> 0;
  return MEMBER_PALETTE[hash % MEMBER_PALETTE.length];
}

function fmtDate(d) {
  return d.toISOString().slice(0, 10);
}

const DAY_LABELS = ["Man", "Tir", "Ons", "Tor", "Fre", "Lør", "Søn"];
const MONTH_LABELS = ["Januar", "Februar", "Mars", "April", "Mai", "Juni", "Juli", "August", "September", "Oktober", "November", "Desember"];

export default function CalendarPage() {
  const { activeBoat, user } = useAuth();
  const navigate = useNavigate();
  const isHousehold = (activeBoat?.kind || "boat") === "household";
  const [monthDate, setMonthDate] = useState(() => { const d = new Date(); d.setDate(1); return d; });
  const [events, setEvents] = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null=create, object=update
  const [importBusy, setImportBusy] = useState(false);
  const [urlDialogOpen, setUrlDialogOpen] = useState(false);
  const [icsUrl, setIcsUrl] = useState("");
  const [urlImportBusy, setUrlImportBusy] = useState(false);
  const [sourcesOpen, setSourcesOpen] = useState(false);

  // Redirect to /home if somehow landed here from boat context
  useEffect(() => {
    if (activeBoat !== null && !isHousehold) {
      toast.info("Kalender er kun i Home Edition");
      navigate("/");
    }
  }, [isHousehold, activeBoat, navigate]);

  // Month bounds
  const [monthStart, monthEnd, days] = useMemo(() => {
    const y = monthDate.getFullYear(), m = monthDate.getMonth();
    const first = new Date(y, m, 1);
    const last = new Date(y, m + 1, 0);
    // Start Monday of first calendar week
    const startDay = (first.getDay() + 6) % 7; // mon=0..sun=6
    const gridStart = new Date(y, m, 1 - startDay);
    const grid = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      grid.push(d);
    }
    return [first, last, grid];
  }, [monthDate]);

  const loadMembers = useCallback(async () => {
    if (!activeBoat?.id) return;
    try {
      // Members are embedded in the boat document; the backend exposes them via GET /api/boats.
      const res = await api.getBoats();
      const current = (res.data || []).find((b) => b.id === activeBoat.id);
      const raw = (current?.members || []).filter((m) => !m.is_global_admin);
      setMembers(raw.map((m) => ({
        id: m.user_id || m.id,
        name: m.name || m.email || "Ukjent",
        color: colorFor(m.user_id || m.id, m.name),
      })));
    } catch { /* ignore */ }
  }, [activeBoat]);

  const loadEvents = useCallback(async () => {
    if (!isHousehold) return;
    setLoading(true);
    try {
      // fetch ±2 months around the visible month so adjacent-month cells stay populated
      const s = new Date(monthDate); s.setMonth(s.getMonth() - 1);
      const e = new Date(monthDate); e.setMonth(e.getMonth() + 2);
      const res = await api.listCalendarEvents(fmtDate(s), fmtDate(e));
      setEvents(res.data.events || []);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.info("Kalender er kun i Home Edition");
        navigate("/");
      } else {
        toast.error("Kunne ikke laste kalender");
      }
    } finally { setLoading(false); }
  }, [monthDate, isHousehold, navigate]);

  useEffect(() => { loadMembers(); }, [loadMembers]);
  useEffect(() => { loadEvents(); }, [loadEvents]);

  const eventsByDate = useMemo(() => {
    const map = {};
    for (const ev of events) {
      const key = ev.date;
      if (!map[key]) map[key] = [];
      map[key].push(ev);
    }
    return map;
  }, [events]);

  const openNew = (date) => {
    setEditing({
      date: fmtDate(date),
      title: "",
      all_day: true,
      time: "",
      location: "",
      notes: "",
      assignee_user_id: "",
    });
    setEditorOpen(true);
  };
  const openEdit = (ev) => { setEditing(ev); setEditorOpen(true); };

  const importIcs = async (file) => {
    setImportBusy(true);
    try {
      const res = await api.importCalendarIcs(file);
      toast.success(`${res.data.imported} avtaler importert (${res.data.skipped} duplikater hoppet over)`);
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import feilet");
    } finally { setImportBusy(false); }
  };

  const importIcsUrl = async () => {
    const url = (icsUrl || "").trim();
    if (!url) { toast.error("Lim inn en kalender-lenke først"); return; }
    setUrlImportBusy(true);
    try {
      const res = await api.importCalendarIcsUrl(url);
      toast.success(`${res.data.imported} avtaler importert (${res.data.skipped} duplikater hoppet over)`);
      setUrlDialogOpen(false);
      setIcsUrl("");
      loadEvents();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke importere fra lenken");
    } finally { setUrlImportBusy(false); }
  };

  const pasteFromClipboard = async () => {
    try {
      const txt = await navigator.clipboard.readText();
      if (txt) setIcsUrl(txt.trim());
    } catch {
      toast.error("Kunne ikke lese fra utklippstavla");
    }
  };

  if (!isHousehold) return null;

  const todayKey = fmtDate(new Date());
  const currentMonthIdx = monthDate.getMonth();

  const upcoming = events.filter((ev) => ev.date >= todayKey).slice(0, 8);

  return (
    <div className="space-y-6" data-testid="calendar-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-stone-900 flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            <Calendar className="h-7 w-7 text-amber-700" />
            Kalender
          </h1>
          <p className="text-sm text-stone-500 mt-1">Avtaler for husstanden — legen, skole, tannlege, fritid</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => setSourcesOpen(true)}
            className="inline-flex items-center justify-center rounded-md border border-amber-200 bg-white px-4 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors gap-2"
            data-testid="open-sources-btn"
            title="Abonnér på flere kalendere og del husstandens egen"
          >
            <Share2 className="h-4 w-4" />
            Kalender-kilder
          </button>
          <label className="inline-flex items-center justify-center rounded-md border border-amber-200 bg-white px-4 py-2.5 text-sm font-medium text-amber-700 hover:bg-amber-50 transition-colors gap-2 cursor-pointer" data-testid="import-ics-label">
            <Upload className="h-4 w-4" />
            {importBusy ? "Importerer…" : "Last opp .ics"}
            <input
              type="file"
              accept=".ics,text/calendar"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) { importIcs(f); e.target.value = ""; } }}
              disabled={importBusy}
              data-testid="import-ics-input"
            />
          </label>
          <button
            onClick={() => openNew(new Date())}
            className="inline-flex items-center justify-center rounded-md bg-amber-600 px-4 py-2.5 text-sm font-medium text-white hover:bg-amber-700 transition-colors gap-2"
            data-testid="new-event-btn"
          >
            <Plus className="h-4 w-4" /> Ny avtale
          </button>
        </div>
      </div>

      {/* Month nav — touch-friendly with swipe support on mobile */}
      <div
        className="flex items-center justify-between bg-white border border-stone-200 rounded-xl px-3 py-2 select-none"
        onTouchStart={(e) => { e.currentTarget.dataset.tx = String(e.touches[0].clientX); }}
        onTouchEnd={(e) => {
          const startX = parseFloat(e.currentTarget.dataset.tx || "NaN");
          if (isNaN(startX)) return;
          const dx = e.changedTouches[0].clientX - startX;
          if (Math.abs(dx) < 60) return;
          setMonthDate((d) => { const n = new Date(d); n.setMonth(n.getMonth() + (dx < 0 ? 1 : -1)); return n; });
        }}
      >
        <button
          onClick={() => setMonthDate((d) => { const n = new Date(d); n.setMonth(n.getMonth() - 1); return n; })}
          className="p-3 hover:bg-stone-100 rounded-md transition-colors active:scale-95"
          data-testid="cal-prev-month"
          aria-label="Forrige måned"
        ><ChevronLeft className="h-5 w-5" /></button>
        <h2 className="text-base sm:text-lg font-semibold text-stone-800" data-testid="cal-month-label">
          {MONTH_LABELS[currentMonthIdx]} {monthDate.getFullYear()}
        </h2>
        <button
          onClick={() => setMonthDate((d) => { const n = new Date(d); n.setMonth(n.getMonth() + 1); return n; })}
          className="p-3 hover:bg-stone-100 rounded-md transition-colors active:scale-95"
          data-testid="cal-next-month"
          aria-label="Neste måned"
        ><ChevronRight className="h-5 w-5" /></button>
      </div>
      <p className="text-[10px] text-stone-400 text-center sm:hidden -mt-4">💡 Sveip måned-linjen for å bytte måned</p>

      {/* Month grid */}
      <div className="bg-white border border-stone-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-7 border-b border-stone-200 bg-stone-50 text-xs font-semibold text-stone-500 uppercase tracking-wide">
          {DAY_LABELS.map((l) => (
            <div key={l} className="px-2 py-2 text-center">{l}</div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const key = fmtDate(d);
            const dayEvents = eventsByDate[key] || [];
            const isCurrentMonth = d.getMonth() === currentMonthIdx;
            const isToday = key === todayKey;
            return (
              <div
                key={key}
                className={`min-h-[100px] border-b border-r border-stone-100 p-1.5 cursor-pointer hover:bg-stone-50/80 transition-colors ${isCurrentMonth ? "bg-white" : "bg-stone-50/40"}`}
                onClick={() => openNew(d)}
                data-testid={`cal-day-${key}`}
              >
                <div className={`text-xs font-semibold mb-1 ${isToday ? "inline-flex items-center justify-center w-6 h-6 bg-amber-600 text-white rounded-full" : isCurrentMonth ? "text-stone-700" : "text-stone-400"}`}>
                  {d.getDate()}
                </div>
                <ul className="space-y-0.5">
                  {dayEvents.slice(0, 3).map((ev) => {
                    const color = colorFor(ev.assignee_user_id, ev.assignee_name);
                    return (
                      <li
                        key={ev.id}
                        onClick={(e) => { e.stopPropagation(); openEdit(ev); }}
                        className={`truncate rounded px-1.5 py-0.5 text-[11px] font-medium ${color.bg} ${color.text} hover:ring-2 hover:ring-offset-1 hover:ring-amber-400`}
                        title={`${ev.title}${ev.time ? " · " + ev.time : ""}${ev.assignee_name ? " · " + ev.assignee_name : ""}`}
                        data-testid={`cal-event-${ev.id}`}
                      >
                        {ev.time && <span className="font-semibold mr-1">{ev.time}</span>}
                        {ev.title}
                      </li>
                    );
                  })}
                  {dayEvents.length > 3 && (
                    <li className="text-[10px] text-stone-500">+ {dayEvents.length - 3} til</li>
                  )}
                </ul>
              </div>
            );
          })}
        </div>
      </div>

      {/* Upcoming list */}
      {upcoming.length > 0 && (
        <div className="bg-white border border-stone-200 rounded-xl p-4" data-testid="cal-upcoming">
          <h3 className="text-sm font-semibold text-stone-700 mb-3 uppercase tracking-wide">Kommende avtaler</h3>
          <ul className="divide-y divide-stone-100">
            {upcoming.map((ev) => {
              const color = colorFor(ev.assignee_user_id, ev.assignee_name);
              const d = new Date(ev.date);
              return (
                <li key={ev.id} className="py-2.5 flex items-center gap-3 cursor-pointer hover:bg-stone-50" onClick={() => openEdit(ev)} data-testid={`cal-upcoming-${ev.id}`}>
                  <div className={`w-2 h-10 rounded-full ${color.dot}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-stone-800 truncate">{ev.title}</div>
                    <div className="text-xs text-stone-500 flex items-center gap-2 flex-wrap">
                      <span>{d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", weekday: "short" })}</span>
                      {ev.time && <><span>·</span><span className="flex items-center gap-1"><Clock className="h-3 w-3" />{ev.time}</span></>}
                      {ev.location && <><span>·</span><span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{ev.location}</span></>}
                      {ev.assignee_name && <><span>·</span><span className={`${color.text} font-medium flex items-center gap-1`}><User className="h-3 w-3" />{ev.assignee_name}</span></>}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <EventEditorDialog
        open={editorOpen}
        onOpenChange={setEditorOpen}
        event={editing}
        members={members}
        onSaved={loadEvents}
        currentUser={user}
      />

      <CalendarSourcesDialog
        open={sourcesOpen}
        onOpenChange={setSourcesOpen}
        onChanged={loadEvents}
      />

      {/* Floating Action Button — mobile only */}
      <button
        onClick={() => openNew(new Date())}
        className="sm:hidden fixed bottom-20 right-4 z-40 w-14 h-14 rounded-full bg-amber-600 text-white shadow-lg shadow-amber-600/30 flex items-center justify-center hover:bg-amber-700 active:scale-95 transition-all"
        data-testid="cal-fab-new"
        aria-label="Ny avtale"
      >
        <Plus className="h-6 w-6" />
      </button>

      {/* Import via URL dialog */}
      <Dialog open={urlDialogOpen} onOpenChange={setUrlDialogOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="import-ics-url-dialog">
          <DialogHeader>
            <DialogTitle>Lim inn kalender-lenke</DialogTitle>
            <DialogDescription>
              Støtter Google Calendar (hemmelig .ics-URL), Outlook, skole-kalendere og andre iCal-abonnement.
              Både <code className="bg-stone-100 px-1 rounded">https://</code> og <code className="bg-stone-100 px-1 rounded">webcal://</code> fungerer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <label className="block text-xs font-medium text-stone-700">Lenke</label>
            <div className="flex gap-2">
              <input
                type="url"
                value={icsUrl}
                onChange={(e) => setIcsUrl(e.target.value)}
                placeholder="https://calendar.google.com/calendar/ical/.../basic.ics"
                className="flex-1 rounded-md border border-stone-300 px-3 py-2 text-sm focus:border-amber-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                data-testid="import-ics-url-input"
                autoFocus
              />
              <button
                type="button"
                onClick={pasteFromClipboard}
                className="inline-flex items-center gap-1 rounded-md border border-stone-300 bg-white px-3 py-2 text-xs font-medium text-stone-700 hover:bg-stone-50"
                data-testid="import-ics-url-paste-btn"
                title="Lim inn fra utklippstavla"
              >
                Lim inn
              </button>
            </div>
            <div className="rounded-md bg-amber-50 border border-amber-100 p-3 text-xs text-amber-900/90 space-y-1">
              <p className="font-semibold">Slik finner du lenken:</p>
              <ul className="list-disc list-inside space-y-0.5">
                <li><b>Google Calendar:</b> Innstillinger → velg kalender → "Hemmelig adresse i iCal-format"</li>
                <li><b>Apple/iCloud:</b> Del kalender → "Offentlig kalender" → kopier URL (webcal://)</li>
                <li><b>Skole/jobb:</b> Be om iCal-abonnementslenken (.ics)</li>
              </ul>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <button
                onClick={() => { setUrlDialogOpen(false); setIcsUrl(""); }}
                className="rounded-md border border-stone-300 bg-white px-4 py-2 text-sm font-medium text-stone-700 hover:bg-stone-50"
                data-testid="import-ics-url-cancel-btn"
              >
                Avbryt
              </button>
              <button
                onClick={importIcsUrl}
                disabled={urlImportBusy || !icsUrl.trim()}
                className="inline-flex items-center gap-2 rounded-md bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="import-ics-url-submit-btn"
              >
                <LinkIcon className="h-4 w-4" />
                {urlImportBusy ? "Henter…" : "Importer"}
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}


function EventEditorDialog({ open, onOpenChange, event, members, onSaved }) {
  const [form, setForm] = useState({ title: "", date: "", all_day: true, time: "", location: "", notes: "", assignee_user_id: "", reminders: [] });
  const [saving, setSaving] = useState(false);
  const isEditing = event && event.id;

  useEffect(() => {
    if (!event) return;
    setForm({
      title: event.title || "",
      date: event.date || fmtDate(new Date()),
      all_day: event.all_day !== false && !event.time,
      time: event.time || "",
      location: event.location || "",
      notes: event.notes || "",
      assignee_user_id: event.assignee_user_id || "",
      reminders: Array.isArray(event.reminders) ? event.reminders : [],
    });
  }, [event]);

  const save = async () => {
    if (!form.title.trim() || !form.date) {
      toast.error("Tittel og dato er påkrevd");
      return;
    }
    setSaving(true);
    try {
      const m = members.find((x) => x.id === form.assignee_user_id);
      const payload = {
        title: form.title.trim(),
        date: form.date,
        all_day: form.all_day || !form.time,
        time: form.all_day ? null : (form.time || null),
        location: form.location?.trim() || null,
        notes: form.notes?.trim() || null,
        assignee_user_id: form.assignee_user_id || null,
        assignee_name: m?.name || null,
        assignee_color: m ? colorFor(m.id, m.name).hex : null,
        reminders: Array.isArray(form.reminders) ? form.reminders : [],
      };
      if (isEditing) {
        await api.updateCalendarEvent(event.id, payload);
        toast.success("Avtale lagret");
      } else {
        await api.createCalendarEvent(payload);
        toast.success("Avtale opprettet");
      }
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!isEditing) return;
    if (!window.confirm(`Slette «${event.title}»?`)) return;
    try {
      await api.deleteCalendarEvent(event.id);
      toast.success("Slettet");
      onSaved?.();
      onOpenChange(false);
    } catch { toast.error("Kunne ikke slette"); }
  };

  const downloadToPhoneCalendar = async () => {
    if (!isEditing) return;
    try {
      // Fetch with auth (axios already attaches Bearer token), get blob
      const res = await axios.get(api.calendarEventIcsUrl(event.id), { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/calendar;charset=utf-8" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${(event.title || "avtale").replace(/[^\w-]/g, "_")}.ics`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => window.URL.revokeObjectURL(url), 2000);
      toast.success("Klikk filen på telefonen for å legge til i kalenderen");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke laste ned");
    }
  };

  if (!event) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[95vh] overflow-y-auto" data-testid="event-editor-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {isEditing ? "Rediger avtale" : "Ny avtale"}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? `Opprettet ${event.created_by_name ? `av ${event.created_by_name}` : ""}`
              : "Legg inn en avtale for noen i husstanden"}
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Tittel *</label>
            <input
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="F.eks. Legen, Tannlege, Fotballtrening"
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              data-testid="event-title-input"
              autoFocus
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase">Dato *</label>
              <input
                type="date"
                value={form.date}
                onChange={(e) => setForm({ ...form, date: e.target.value })}
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
                data-testid="event-date-input"
              />
            </div>
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase">Tid</label>
              <input
                type="time"
                value={form.time}
                onChange={(e) => setForm({ ...form, time: e.target.value, all_day: !e.target.value })}
                disabled={form.all_day}
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none disabled:opacity-50"
                data-testid="event-time-input"
              />
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm text-stone-700 cursor-pointer">
            <Checkbox
              checked={form.all_day}
              onCheckedChange={(checked) => setForm({ ...form, all_day: !!checked, time: checked ? "" : form.time })}
              data-testid="event-allday-checkbox"
            />
            Hele dagen
          </label>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Hvem</label>
            <select
              value={form.assignee_user_id}
              onChange={(e) => setForm({ ...form, assignee_user_id: e.target.value })}
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              data-testid="event-assignee-select"
            >
              <option value="">Alle i husstanden</option>
              {members.map((m) => (
                <option key={m.id} value={m.id}>{m.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Sted</label>
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Klinikk, skole, adresse..."
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none"
              data-testid="event-location-input"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-stone-500 uppercase">Notat</label>
              <SpellCheckButton
                value={form.notes}
                onAccept={(corrected) => setForm({ ...form, notes: corrected })}
                context="kalender"
                size="xs"
                testid="event-notes-spell-btn"
                label="AI staveretter"
              />
            </div>
            <textarea
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Husk pass, kjørebeskjed, telefon..."
              className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none min-h-[60px]"
              data-testid="event-notes-input"
            />
          </div>
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase">Påminnelser</label>
            <p className="text-[11px] text-stone-400 mt-0.5 mb-2">Velg når du vil få varsel før avtalen starter</p>
            <div className="flex flex-wrap gap-1.5">
              {[
                { m: 15, label: "15 min" },
                { m: 60, label: "1 time" },
                { m: 360, label: "6 timer" },
                { m: 1440, label: "1 dag" },
                { m: 10080, label: "1 uke" },
              ].map(({ m, label }) => {
                const active = (form.reminders || []).includes(m);
                return (
                  <button
                    key={m}
                    type="button"
                    onClick={() => {
                      const curr = new Set(form.reminders || []);
                      if (active) curr.delete(m); else curr.add(m);
                      setForm({ ...form, reminders: Array.from(curr).sort((a, b) => a - b) });
                    }}
                    className={`inline-flex items-center gap-1 rounded-full px-3 py-1.5 text-xs font-medium border transition-all ${active ? "bg-amber-600 text-white border-amber-700 shadow-sm" : "bg-white text-stone-600 border-stone-200 hover:border-amber-400 hover:text-amber-700"}`}
                    data-testid={`event-reminder-${m}`}
                  >
                    {label} før
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex gap-2 pt-2 flex-wrap">
            <button
              onClick={() => onOpenChange(false)}
              className="rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              data-testid="event-cancel-btn"
            >
              <X className="h-4 w-4" />
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-amber-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-amber-700 transition-colors disabled:opacity-50"
              data-testid="event-save-btn"
            >
              <Save className="h-4 w-4" /> {saving ? "Lagrer…" : isEditing ? "Lagre" : "Opprett"}
            </button>
            {isEditing && (
              <button
                onClick={remove}
                className="rounded-md border border-red-200 bg-white p-2.5 text-red-600 hover:bg-red-50 transition-colors"
                data-testid="event-delete-btn"
                title="Slett avtale"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
          {isEditing && (
            <button
              onClick={downloadToPhoneCalendar}
              className="w-full inline-flex items-center justify-center gap-2 rounded-md border border-sky-300 bg-sky-50 px-4 py-2.5 text-sm font-medium text-sky-800 hover:bg-sky-100 transition-colors"
              data-testid="event-add-to-phone-btn"
              title="Last ned som .ics-fil — trykk filen på telefonen for å legge til i telefonens kalender"
            >
              <Smartphone className="h-4 w-4" /> Legg til i telefonens kalender
            </button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
